/* HX chart system — custom SVG, no libraries. Smooth Catmull-Rom curves,
   draw-in animation, soft dashed grid, polished peak tooltip. */
import { HX_COLORS as C } from "./tokens";

/** Catmull-Rom → cubic Bézier: genuinely smooth through-points curve. */
export function smoothPath(pts, tension = 1) {
  if (pts.length < 2) return "";
  if (pts.length === 2) return `M ${pts[0][0]},${pts[0][1]} L ${pts[1][0]},${pts[1][1]}`;
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + ((p2[0] - p0[0]) / 6) * tension;
    const c1y = p1[1] + ((p2[1] - p0[1]) / 6) * tension;
    const c2x = p2[0] - ((p3[0] - p1[0]) / 6) * tension;
    const c2y = p2[1] - ((p3[1] - p1[1]) / 6) * tension;
    d += ` C ${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0]},${p2[1]}`;
  }
  return d;
}

export function HxSparkline({ data = [], color = C.blue, animate = true }) {
  const w = 260, h = 46;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => [8 + (i * (w - 16)) / Math.max(data.length - 1, 1), h - 9 - (v / max) * (h - 20)]);
  const d = smoothPath(pts);
  const id = `hxsg-${color.replace("#", "")}`;
  return (
    <svg className="hx-kpi__spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.20" />
          <stop offset="80%" stopColor={color} stopOpacity="0.015" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {pts.length > 1 && (
        <path className={animate ? "hx-area-fade" : ""} d={`${d} L ${pts[pts.length - 1][0]},${h} L ${pts[0][0]},${h} Z`} fill={`url(#${id})`} />
      )}
      {pts.length > 1 && (
        <path className={animate ? "hx-line-draw" : ""} pathLength="1" d={d} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      )}
    </svg>
  );
}

/** days: [{ day, inbound, outbound, missed }] */
export function HxAreaChart({ days, testid = "call-volume-chart" }) {
  const w = 680, h = 230, padL = 34, padR = 16, padT = 20, padB = 30;
  const totals = days.map((d) => d.inbound + d.outbound + d.missed);
  const max = Math.max(...totals, 4);
  const x = (i) => padL + (i * (w - padL - padR)) / Math.max(days.length - 1, 1);
  const y = (v) => padT + (1 - v / max) * (h - padT - padB);
  const pts = totals.map((v, i) => [x(i), y(v)]);
  const d = smoothPath(pts);
  const gridVals = [0, Math.round(max / 2), max];
  const peak = totals.indexOf(Math.max(...totals));
  const label = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });
  return (
    <svg viewBox={`0 0 ${w} ${h}`} data-testid={testid}>
      <defs>
        <linearGradient id="hxvol" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.blue} stopOpacity="0.24" />
          <stop offset="70%" stopColor={C.blue} stopOpacity="0.04" />
          <stop offset="100%" stopColor={C.blue} stopOpacity="0" />
        </linearGradient>
        <filter id="hxtip" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#0B1526" floodOpacity="0.28" />
        </filter>
      </defs>
      {gridVals.map((g) => (
        <g key={g}>
          <line className="hx-chart-grid" x1={padL} x2={w - padR} y1={y(g)} y2={y(g)} />
          <text x={padL - 8} y={y(g) + 4} textAnchor="end" fontSize="10" fill="#98A2B3" fontFamily="IBM Plex Mono, monospace">{g}</text>
        </g>
      ))}
      {pts.length > 1 && <path className="hx-area-fade" d={`${d} L ${pts[pts.length - 1][0]},${y(0)} L ${pts[0][0]},${y(0)} Z`} fill="url(#hxvol)" />}
      {pts.length > 1 && <path className="hx-line-draw" pathLength="1" d={d} fill="none" stroke={C.blue} strokeWidth="2.6" strokeLinecap="round" />}
      {pts.map(([px, py], i) => (
        <g key={i}>
          <circle className="hx-dot-pop" style={{ animationDelay: `${0.7 + i * 0.06}s` }} cx={px} cy={py} r={i === peak ? 5 : 3.4} fill="#fff" stroke={C.blue} strokeWidth="2.2">
            <title>{`${label(days[i].day)} — ${totals[i]} calls (${days[i].inbound} in / ${days[i].outbound} out / ${days[i].missed} missed)`}</title>
          </circle>
          <text x={px} y={h - 10} textAnchor="middle" fontSize="10.5" fill="#98A2B3" fontWeight="600" fontFamily="Manrope, sans-serif">{label(days[i].day)}</text>
        </g>
      ))}
      {peak >= 0 && totals[peak] > 0 && (() => {
        const below = pts[peak][1] - 34 < 2;
        const by = below ? pts[peak][1] + 12 : pts[peak][1] - 32;
        return (
          <g className="hx-dot-pop" style={{ animationDelay: "1.15s" }} filter="url(#hxtip)">
            <rect x={pts[peak][0] - 21} y={by} width="42" height="21" rx="8" fill="#0B1526" />
            <text x={pts[peak][0]} y={by + 14.5} textAnchor="middle" fontSize="10.5" fill="#fff" fontWeight="700" fontFamily="Manrope, sans-serif">{totals[peak]}</text>
          </g>
        );
      })()}
    </svg>
  );
}
